import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Mail, AlertCircle } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import { useTranslation } from "react-i18next";

export const EmailSettingsTab = () => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const [senderName, setSenderName] = useState("");
	const [senderAddress, setSenderAddress] = useState("");
	const [signature, setSignature] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);

	useEffect(() => {
		if (user?.id)
			loadSettings();
	}, [user?.id]);

	const loadSettings = async () => {
		if (!user?.id) return;
		setIsLoading(true);
		try {
			const { data } = await supabase
				.from('profiles')
				.select('settings')
				.eq('id', user.id)
				.single();

			if (data?.settings) {
				const s = data.settings as any;
				setSenderName(s.email_sender_name || "");
				setSenderAddress(s.email_sender_address || "");
				setSignature(s.email_signature || "");
			}
		} catch (_error) {
			toast({
				title: t("common.error", "Error"),
				description: t("settings.email.loadError", "Failed to load email settings"),
				variant: "destructive",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleSave = async () => {
		if (!user?.id) return;
		setIsSaving(true);
		try {
			const { data } = await supabase
				.from('profiles')
				.select('settings')
				.eq('id', user.id)
				.single();

			const currentSettings = (data?.settings as any) || {};
			const updatedSettings = {
				...currentSettings,
				email_sender_name: senderName.trim() || null,
				email_sender_address: senderAddress.trim() || null,
				email_signature: signature.trim() || null,
			};

			const { error } = await supabase
				.from('profiles')
				.update({ settings: updatedSettings })
				.eq('id', user.id);

			if (error) throw error;

			toast({
				title: t("common.success", "Success"),
				description: t("settings.email.saveSuccess", "Email settings saved"),
			});
		} catch (_error) {
			toast({
				title: t("common.error", "Error"),
				description: t("settings.email.saveError", "Failed to save email settings"),
				variant: "destructive",
			});
		} finally {
			setIsSaving(false);
		}
	};

	const quillModules = {
		toolbar: [
			["bold", "italic", "underline"],
			[{ list: "ordered" }, { list: "bullet" }],
			["link"],
			["clean"],
		],
	};

	if (isLoading)
		return (
			<div className="flex items-center justify-center py-12">
				<Loader2 className="h-6 w-6 animate-spin" />
			</div>
		);

	return (
		<div className="space-y-6">
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center gap-2">
						<Mail className="h-5 w-5" />
						{t("settings.email.senderTitle", "Sender Settings")}
					</CardTitle>
					<CardDescription>
						{t("settings.email.senderDescription", "Configure how your outbound emails appear to recipients")}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="space-y-2">
						<Label htmlFor="sender-name">
							{t("settings.email.senderName", "Sender Name")}
						</Label>
						<Input
							id="sender-name"
							value={senderName}
							onChange={(e) => setSenderName(e.target.value)}
							placeholder={t("settings.email.senderNamePlaceholder", "e.g. Kevin from Digger Recruitment")}
						/>
						<p className="text-xs text-muted-foreground">
							{t("settings.email.senderNameHelp", "The name that appears in the 'From' field. Leave empty to use 'Digger Recruitment'.")}
						</p>
					</div>

					<div className="space-y-2">
						<Label htmlFor="sender-address">
							{t("settings.email.senderAddress", "Sender Email Address")}
						</Label>
						<Input
							id="sender-address"
							type="email"
							value={senderAddress}
							onChange={(e) => setSenderAddress(e.target.value)}
							placeholder="noreply@yourdomain.com"
						/>
						<div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-sm">
							<AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
							<p className="text-xs text-amber-700 dark:text-amber-300">
								{t("settings.email.senderAddressHelp", "To use a custom sender address, you must first verify your domain in the Resend dashboard (resend.com/domains). Without a verified domain, emails are sent from the default Digger address.")}
							</p>
						</div>
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>
						{t("settings.email.signatureTitle", "Email Signature")}
					</CardTitle>
					<CardDescription>
						{t("settings.email.signatureDescription", "Your personal signature, automatically appended to emails you send")}
					</CardDescription>
				</CardHeader>
				<CardContent>
					<div className="border rounded-sm overflow-hidden">
						<ReactQuill
							value={signature}
							onChange={setSignature}
							modules={quillModules}
							placeholder={t("settings.email.signaturePlaceholder", "e.g. Best regards, Kevin - Digger Recruitment - +32 123 456 789")}
							style={{ minHeight: "150px" }}
						/>
					</div>
				</CardContent>
				<CardFooter className="flex justify-end">
					<Button onClick={handleSave} disabled={isSaving}>
						{isSaving ? (
							<>
								<Loader2 className="h-4 w-4 mr-2 animate-spin" />
								{t("common.saving", "Saving...")}
							</>
						) : (
							<>
								<Save className="h-4 w-4 mr-2" />
								{t("common.save", "Save")}
							</>
						)}
					</Button>
				</CardFooter>
			</Card>
		</div>
	);
};
